import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, TrendingUp, TrendingDown, Minus, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChapterRow = ({ ch, testId }) => {
  const delta = ch.improvement_delta || 0;
  const positive = delta > 0;
  const flat = delta === 0 || !ch.is_complete;
  const Icon = positive ? TrendingUp : flat ? Minus : TrendingDown;
  const tone = positive ? 'text-emerald-600' : flat ? 'text-gray-500' : 'text-red-600';
  const bg = positive ? 'bg-emerald-50 border-emerald-200' : flat ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-200';
  return (
    <div className={`rounded-lg border p-4 ${bg}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">{ch.subject}</p>
          <p className="font-semibold text-gray-800 truncate" title={ch.chapter}>{ch.chapter}</p>
        </div>
        <div className={`flex items-center gap-1 ${tone}`}>
          <Icon size={18} />
          <span className="font-bold">{delta > 0 ? '+' : ''}{delta}%</span>
        </div>
      </div>
      <div className="flex items-center justify-between text-sm">
        <div>
          <span className="text-gray-500">Before </span>
          <span className="font-semibold text-gray-800">{ch.baseline_score}%</span>
          <span className="text-gray-400 mx-1">→</span>
          <span className="text-gray-500">After </span>
          <span className="font-semibold text-gray-800">{ch.post_score}%</span>
        </div>
        <span className="text-[11px] text-gray-400">{ch.attempts_count} attempt{ch.attempts_count !== 1 ? 's' : ''}</span>
      </div>
    </div>
  );
};

const StudentProgressWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/student/chapter-progress`);
        setData(res.data);
      } catch (e) {
        console.error('progress widget load error', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 flex items-center justify-center" data-testid="progress-widget-loading">
        <Loader2 className="animate-spin text-teal-500" size={22} />
      </div>
    );
  }

  const chapters = data?.chapters || [];
  const summary = data?.summary || { total_chapters: 0, completed_chapters: 0, avg_baseline: 0, avg_post: 0, avg_improvement: 0 };
  const positive = (summary.avg_improvement || 0) > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="student-progress-widget">
      <div className={`px-6 py-5 text-white ${positive ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' : 'bg-gradient-to-r from-slate-600 to-slate-700'}`}>
        <div className="flex items-center gap-2 mb-3">
          <BookOpen size={22} />
          <h3 className="font-semibold text-lg">My Progress — Before → After</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white/15 rounded-lg p-3">
            <p className="text-white/70 text-xs uppercase tracking-wide">Chapters</p>
            <p className="text-2xl font-bold">{summary.total_chapters}</p>
          </div>
          <div className="bg-white/15 rounded-lg p-3">
            <p className="text-white/70 text-xs uppercase tracking-wide">Avg Before</p>
            <p className="text-2xl font-bold">{summary.avg_baseline}%</p>
          </div>
          <div className="bg-white/15 rounded-lg p-3">
            <p className="text-white/70 text-xs uppercase tracking-wide">Avg After</p>
            <p className="text-2xl font-bold">{summary.avg_post}%</p>
          </div>
          <div className="bg-white/15 rounded-lg p-3">
            <p className="text-white/70 text-xs uppercase tracking-wide">Improvement</p>
            <p className="text-2xl font-bold">{summary.avg_improvement > 0 ? '+' : ''}{summary.avg_improvement}%</p>
          </div>
        </div>
      </div>
      <div className="p-4">
        {chapters.length === 0 ? (
          <p className="text-center text-gray-400 py-8 text-sm">
            No chapter progress yet. Attempt Topic Tests to start tracking your improvement.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {chapters.map((c, i) => (
              <ChapterRow key={`${c.subject}-${c.chapter}-${i}`} ch={c} testId={`progress-chapter-${i}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentProgressWidget;
